import React, { useEffect, useState } from "react";
import { flattenIndexToList } from "../utils/structures";
import { getAllPublicStructures } from "../firebase/firestore";
import type { SimpleIndexEntry } from "../types/structures";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ArrowLeft, MapPin, Building2, Info } from "lucide-react";
import HubHeader from "./hub/HubHeader";

interface TravelersHubProps {
  onBack: () => void;
  onOpenPassport?: (slug: string) => void;
}

export default function TravelersHub({ onBack, onOpenPassport }: TravelersHubProps) {
  const [items, setItems] = useState<SimpleIndexEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Prefer Firestore public list
        const docs = await getAllPublicStructures();
  // Debug logs removed for production cleanliness
        
        // DEBUG: Log the structure of the first document
        if (docs && docs.length > 0) {
          // Debug logs removed for production cleanliness
          if (docs[0].modules) {
            // Debug logs removed for production cleanliness
          }
          if (docs[0].structure) {
            // Debug logs removed for production cleanliness
          }
        }
        
        let list: SimpleIndexEntry[] = [];
        if (Array.isArray(docs) && docs.length) {
          list = docs.map((d: any, index: number) => {
            // Debug logs removed for production cleanliness
            
            const s = d?.structure || {};
            const acc = s.accommodation_data || {};
            const addr = acc.address || {};
            
            // Debug logs removed for production cleanliness
            
            // PRIORITIZE "NOME STRUTTURA" (6.1) from "Dati info azienda" module
            let name = '';
            let type = acc.type || d.type;
            let address = addr.street || addr.city || addr.province;
            
            // Check first module for "NOME STRUTTURA" (6.1) first
            if (Array.isArray(d.modules) && d.modules.length > 0) {
              const firstModule = d.modules[0];
              // Debug logs removed for production cleanliness
              
              if (firstModule?.content) {
                const content = firstModule.content;
                
                // Extract general information from first module - prioritize NOME STRUTTURA
                // Try both numbered key (6.1) and human-readable key (NOME STRUTTURA)
                if (content["6.1"]) {
                  name = content["6.1"];
                  // Debug logs removed for production cleanliness
                } else if (content["NOME STRUTTURA"]) {
                  name = content["NOME STRUTTURA"];
                  // Debug logs removed for production cleanliness
                } else {
                  // Debug logs removed for production cleanliness
                }
                if (!type && content["7.1"]) type = content["7.1"];
                if (!address && content["9.1"]) address = content["9.1"];
              }
            } else {
              // Debug logs removed for production cleanliness
            }
            
            // Fallback to standard accommodation_name if no module name found
            if (!name) {
              name = acc.accommodation_name || d.name;
              // Debug logs removed for production cleanliness
            }
            
            // Extract locality from address
            let locality = addr.city || addr.province;
            if (!locality && address) {
              // Try to extract city from address string
              const addressParts = address.split(',');
              if (addressParts.length > 1) {
                locality = addressParts[addressParts.length - 1].trim();
              }
            }
            
            const result = {
              slug: String(d.slug || d.id || ""),
              name: String(name || d.slug || d.id || ""),
              type: String(type || ""),
              locality: String(locality || ""),
            };
            
            // Debug logs removed for production cleanliness
            
            return result;
          });
        } else {
          // Fallback to static index
          // Debug logs removed for production cleanliness
          list = await flattenIndexToList();
        }
        if (mounted) setItems(list);
      } catch (e: any) {
        if (mounted) setError(e?.message || "Failed to load public structures");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50">
      <HubHeader
        onGoHome={onBack}
        centerSlot={
          <div className="text-white/95 text-s font-medium px-3 py-1.5 rounded bg-white/10 backdrop-blur-sm border border-white/30 hidden sm:inline-flex items-center gap-2 hover:border-emerald-200 hover:shadow-lg hover:shadow-emerald-500/20 transition">
            Hub dei viaggiatori
          </div>
        }
      />
      <div className="max-w-6xl mx-auto space-y-6 p-4">

        <div className="text-center space-y-2">
          <h1 className="text-xxl font-semibold">Hub dei viaggiatori</h1>
          <p className="text-muted-foreground">Sfoglia tutti i Passaporti di Accessibilità pubblici. Nessun accesso richiesto.</p>
        </div>
        {/* Explanatory Card */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-blue-900 mb-2">Cos'è un Passaporto di Accessibilità?</h3>
                <p className="text-blue-800 text-s leading-relaxed">
                  Questo profilo pubblico in sola lettura riassume le caratteristiche di accessibilità di una struttura e gli eventuali vincoli,
                  organizzati per categorie chiare (es. motoria, sensoriale, comunicazione). Aiuta i viaggiatori a capire rapidamente se un luogo
                  risponde alle loro esigenze prima della visita.
                </p>
                <div className="mt-4 flex items-center gap-4 text-ss text-blue-700">
                  <span>• Chiarezza per categoria</span>
                  <span>• Note trasparenti sui vincoli</span>
                  <span>• Vista condivisibile — nessun accesso richiesto</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
{loading && (
          <Card>
            <CardHeader>
              <CardTitle>Caricamento strutture…</CardTitle>
              <CardDescription>Attendere mentre recuperiamo la lista pubblica.</CardDescription>
            </CardHeader>
          </Card>
        )}

        {error && (
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="text-red-700">Impossibile caricare</CardTitle>
              <CardDescription className="text-red-600">{error}</CardDescription>
            </CardHeader>
          </Card>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it) => (
              <Card key={it.slug} className="hover:shadow-lg transition-shadow">
                <CardHeader className="space-y-1">
                  <div className="flex items-start justify-between gap-3">
                    <CardTitle className="text-m leading-snug flex items-center gap-2">
                      <span className="w-6 h-6 rounded bg-gradient-travelin-green flex items-center justify-center">
                        <Building2 className="w-3.5 h-3.5 text-white" />
                      </span>
                      {it.name}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {it.type ? <Badge variant="secondary">{it.type}</Badge> : null}
                    {it.locality ? (
                      <span className="inline-flex items-center text-ss text-muted-foreground gap-1">
                        <MapPin className="w-3 h-3" /> {it.locality}
                      </span>
                    ) : null}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {onOpenPassport ? (
                    <Button className="w-full btn-gradient-green text-white shadow-md hover:shadow-lg transition-all" onClick={() => onOpenPassport(it.slug)}>
                      Visualizza Passaporto di Accessibilità
                    </Button>
                  ) : (
                    <a
                      className="inline-block text-s text-primary hover:underline"
                      href={`/detailed_structures/${it.slug}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Apri JSON grezzo
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
